import { useEffect, useRef, type ReactNode } from 'react';
import { LAYOUT_CATALOG_DRAG_MIME } from './layoutCatalogDrag';

export interface LayoutCatalogItemProps {
  catalogItemId: string;
  disabled?: boolean;
  children?: ReactNode;
  className?: string;
  onInsertRequest?: (event: { catalogItemId: string }) => void;
  onDragStateChange?: (event: { catalogItemId: string; active: boolean }) => void;
}

export function LayoutCatalogItem({ catalogItemId, disabled = false, children, className, onInsertRequest, onDragStateChange }: LayoutCatalogItemProps) {
  if (typeof catalogItemId !== 'string' || !catalogItemId.trim() || catalogItemId.length > 256 || /[\u0000-\u001f\u007f-\u009f]/.test(catalogItemId) || ['__proto__', 'prototype', 'constructor'].includes(catalogItemId)) {
    throw new TypeError('Invalid layout catalog item ID.');
  }
  const active = useRef<{ catalogItemId: string; notify: LayoutCatalogItemProps['onDragStateChange'] }>();
  const suppressClick = useRef(false);
  const finish = () => {
    const previous = active.current;
    active.current = undefined;
    if (previous) previous.notify?.({ catalogItemId: previous.catalogItemId, active: false });
  };
  useEffect(() => () => finish(), [catalogItemId, disabled]);
  return <button type="button" className={className} disabled={disabled} draggable={!disabled}
    onPointerDown={() => { if (!active.current) suppressClick.current = false; }}
    onKeyDown={event => { if (!active.current && (event.key === 'Enter' || event.key === ' ')) suppressClick.current = false; }}
    onClick={event => {
      if (disabled || suppressClick.current) { event.preventDefault(); return; }
      onInsertRequest?.({ catalogItemId });
    }}
    onDragStart={event => {
      if (disabled) { event.preventDefault(); return; }
      finish();
      event.dataTransfer.clearData();
      event.dataTransfer.setData(LAYOUT_CATALOG_DRAG_MIME, catalogItemId);
      event.dataTransfer.effectAllowed = 'copy';
      suppressClick.current = true;
      active.current = { catalogItemId, notify: onDragStateChange };
      onDragStateChange?.({ catalogItemId, active: true });
    }}
    onDragEnd={finish}
  >{children}</button>;
}
